import { Component, OnInit, OnDestroy, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateFormatPipe, StatusBadgePipe } from '../../../../shared/pipes';
import { EmailReportModalComponent } from '../../../../shared/components/email-report-modal/email-report-modal.component';
import { StateReport, StateReportStatus, CreateStateReportRequest } from '../../../../core/models/state-report.model';
import { StateReportService } from '../../../../core/services/state-report.service';
import { OrganizationContextService } from '../../../../core/services/organization-context.service';
import { ToastService } from '../../../../core/services/toast.service';

/**
 * State Reports Component
 *
 * Displays and manages state compliance reports for the organization.
 * Integrates with real backend API via StateReportService and receives
 * real-time progress updates via WebSocket.
 *
 * Reference: /agent-os/specs/2026-01-16-state-reports/spec.md
 */
@Component({
  selector: 'app-state-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, DateFormatPipe, StatusBadgePipe, EmailReportModalComponent],
  templateUrl: './state-reports.component.html',
  styleUrls: ['./state-reports.component.scss']
})
export class StateReportsComponent implements OnInit, OnDestroy {
  // Inject services
  private stateReportService = inject(StateReportService);
  private organizationContext = inject(OrganizationContextService);
  private toastService = inject(ToastService);

  // Shared email modal (drives its own form state; we drive its async feedback)
  @ViewChild(EmailReportModalComponent) emailModal?: EmailReportModalComponent;

  // Signal-based state from service (bound to real API data)
  readonly recentReports = this.stateReportService.reports;
  readonly isLoading = this.stateReportService.isLoading;
  readonly error = this.stateReportService.error;
  readonly reportTypes = this.stateReportService.reportTypes;
  readonly states = this.stateReportService.usStates;

  // Local UI state signals
  isGenerating = signal(false);
  deleteError = signal<string | null>(null);

  // Form fields (local state)
  startDate: string = '';
  endDate: string = '';
  selectedState: string = '';
  /**
   * Pinned to 'all'. The picker was removed because the report-type list and
   * the spray-record `application_types` list were never reconciled, so three
   * of four filters silently removed records from a compliance filing — most
   * seriously `pesticide`, which omitted herbicide and fungicide applications.
   *
   * Kept as a field rather than inlined: it still feeds the generate payload,
   * the generated report name, and the rendering of historical rows that carry
   * a specific type. Restoring the picker is a template change once the
   * vocabularies are reconciled.
   */
  reportType: string = 'all';
  includeChemicalDetails: boolean = true;
  includeApplicationRates: boolean = true;
  includeFieldMaps: boolean = true;
  includeWeatherConditions: boolean = true;

  // Preview modal state
  showPreviewModal = signal(false);
  previewReport = signal<StateReport | null>(null);
  previewLoading = signal(false);

  // Email modal state (which report; the modal owns the rest)
  showEmailModal = signal(false);
  emailReport = signal<StateReport | null>(null);

  // Delete confirmation state
  showDeleteConfirm = signal(false);
  reportToDelete = signal<StateReport | null>(null);
  deleteLoading = signal(false);

  ngOnInit(): void {
    this.setDefaultDates();
    this.loadReports();
  }

  ngOnDestroy(): void {
    // Service cleanup is handled by the service's OnDestroy
  }

  /**
   * Load reports from API
   */
  private async loadReports(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('No organization ID available, cannot load state reports');
      return;
    }

    await this.stateReportService.loadReports(orgId);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.stateReportService.clearError();
  }

  /**
   * Retry loading reports after an error
   */
  retryLoad(): void {
    this.clearError();
    this.loadReports();
  }

  /**
   * Set default date range (last 30 days)
   */
  setDefaultDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    this.endDate = today.toISOString().split('T')[0];
    this.startDate = thirtyDaysAgo.toISOString().split('T')[0];
  }

  /**
   * Reset the generate report form to defaults
   */
  resetForm(): void {
    this.setDefaultDates();
    this.selectedState = '';
    this.reportType = 'all';
    this.includeChemicalDetails = true;
    this.includeApplicationRates = true;
    this.includeFieldMaps = true;
    this.includeWeatherConditions = true;
  }

  /**
   * Generate a new report
   * Creates a draft report and immediately triggers PDF generation
   */
  async generateReport(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.error('No organization ID available');
      return;
    }

    if (!this.canGenerate()) return;

    this.isGenerating.set(true);

    try {
      // Create the report request
      const request: CreateStateReportRequest = {
        name: this.getReportName(),
        state: this.selectedState,
        startDate: this.startDate,
        endDate: this.endDate,
        reportType: this.reportType,
        options: {
          includeChemicalDetails: this.includeChemicalDetails,
          includeApplicationRates: this.includeApplicationRates,
          includeFieldMaps: this.includeFieldMaps,
          includeWeatherConditions: this.includeWeatherConditions
        }
      };

      // Create draft report via API
      const newReport = await this.stateReportService.createAndAddReport(orgId, request);

      if (newReport) {
        // Immediately trigger PDF generation
        await this.stateReportService.triggerPdfGeneration(orgId, newReport.id);
        // Reset form after successful generation start
        this.resetForm();
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Generate PDF for an existing draft report
   */
  async generatePdf(report: StateReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'draft') return;

    await this.stateReportService.triggerPdfGeneration(orgId, report.id);
  }

  /**
   * Download report PDF
   * Gets fresh pre-signed URL and opens in new tab
   */
  async downloadReport(report: StateReport): Promise<void> {
    if (report.status !== 'ready' && report.status !== 'submitted') return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    try {
      // Fetch fresh report data to get a valid pre-signed URL
      const freshReport = await this.stateReportService.fetchReport(orgId, report.id);

      // Use downloadUrl (pre-signed URL) for download
      if (freshReport?.downloadUrl) {
        window.open(freshReport.downloadUrl, '_blank');
      } else {
        console.error('No download URL available');
      }
    } catch (error) {
      console.error('Error fetching report for download:', error);
    }
  }

  /**
   * View report in preview modal
   */
  viewReport(report: StateReport): void {
    this.previewReport.set(report);
    this.showPreviewModal.set(true);
    this.previewLoading.set(true);

    // Load fresh report data for preview
    const orgId = this.organizationContext.currentOrganizationId();
    if (orgId) {
      this.stateReportService.fetchReport(orgId, report.id).then(freshReport => {
        if (freshReport) {
          this.previewReport.set(freshReport);
        }
        this.previewLoading.set(false);
      }).catch(() => {
        this.previewLoading.set(false);
      });
    } else {
      this.previewLoading.set(false);
    }
  }

  closePreviewModal(): void {
    this.showPreviewModal.set(false);
    this.previewReport.set(null);
  }

  /**
   * Open the agency-submission modal.
   *
   * Allowed for `submitted` reports too, so a re-send is possible — nothing was actually sent
   * by ADN, so "already submitted" is the user's own assertion and must not lock them out.
   */
  openEmailModal(report: StateReport): void {
    if (report.status !== 'ready' && report.status !== 'submitted') return;
    this.emailReport.set(report);
    this.showEmailModal.set(true);
  }

  closeEmailModal(): void {
    this.showEmailModal.set(false);
    this.emailReport.set(null);
  }

  /**
   * Report name shown in the email modal
   */
  emailReportTitle(): string {
    const report = this.emailReport();
    if (!report) return '';
    return report.name || this.getReportTypeLabel(report.reportType || '');
  }

  /**
   * State and date range shown in the email modal
   */
  emailReportSubtitle(): string {
    const report = this.emailReport();
    if (!report) return '';
    return `${this.getStateLabel(report.state || '')} • ${this.formatDateRange(report.startDate, report.endDate)}`;
  }

  /**
   * Download the report the email modal is composing for.
   * Reuses the existing fresh-presigned-URL flow unchanged.
   */
  onEmailDownloadRequested(): void {
    const report = this.emailReport();
    if (!report) return;
    void this.downloadReport(report);
  }

  /**
   * Record the report as submitted to the chosen recipient.
   *
   * ADN sends nothing — the user sends the message from their own mail client and then tells us.
   * This hits the existing `POST /:id/submit` endpoint unchanged; `submitted_to` already stores a
   * recipient address, which is precisely what we need it to do.
   */
  async onEmailSubmitted(email: string): Promise<void> {
    const report = this.emailReport();
    if (!report) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    this.emailModal?.setProcessing(true);
    this.emailModal?.setError(null);

    try {
      const success = await this.stateReportService.submitAndUpdateReport(orgId, report.id, email);
      if (success) {
        this.closeEmailModal();
        this.toastService.success(`Report marked as submitted to ${email}.`);
      } else {
        this.emailModal?.setError('Failed to submit report. Please try again.');
      }
    } catch (error: any) {
      console.error('Error submitting report:', error);
      this.emailModal?.setError(error.message || 'Failed to submit report. Please try again.');
    } finally {
      this.emailModal?.setProcessing(false);
    }
  }

  /**
   * Open delete confirmation modal
   */
  confirmDelete(report: StateReport): void {
    this.deleteError.set(null);
    this.reportToDelete.set(report);
    this.showDeleteConfirm.set(true);
  }

  cancelDelete(): void {
    this.showDeleteConfirm.set(false);
    this.reportToDelete.set(null);
    this.deleteError.set(null);
    this.deleteLoading.set(false);
  }

  /**
   * Delete report
   */
  async deleteReport(): Promise<void> {
    const report = this.reportToDelete();
    if (!report) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    // Cannot delete while generating
    if (report.status === 'generating') {
      this.deleteError.set('Cannot delete a report while it is generating');
      return;
    }

    this.deleteLoading.set(true);
    this.deleteError.set(null);

    try {
      const success = await this.stateReportService.deleteAndRemoveReport(orgId, report.id);
      if (success) {
        this.cancelDelete();
      } else {
        this.deleteError.set('Failed to delete report');
      }
    } catch (error: any) {
      console.error('Error deleting report:', error);
      // Handle 400 error for generating status
      if (error.status === 400 || error.message?.includes('generating')) {
        this.deleteError.set('Cannot delete a report while it is generating');
      } else {
        this.deleteError.set(error.message || 'Failed to delete report');
      }
    } finally {
      this.deleteLoading.set(false);
    }
  }

  /**
   * Retry a failed report
   */
  async retryReport(report: StateReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'failed') return;

    await this.stateReportService.retryFailedReport(orgId, report.id);
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: string): string {
    return this.stateReportService.getStatusText(status as StateReportStatus);
  }

  /**
   * Get Bootstrap icon class for status
   */
  getStatusIcon(status: string): string {
    return this.stateReportService.getStatusIcon(status as StateReportStatus);
  }

  /**
   * Get report type label from value
   */
  getReportTypeLabel(value: string): string {
    return this.stateReportService.getReportTypeLabel(value);
  }

  /**
   * Get state name from code
   */
  getStateLabel(code: string): string {
    return this.stateReportService.getStateLabel(code);
  }

  /**
   * Format date range for display
   */
  formatDateRange(startDate: string, endDate: string): string {
    return this.stateReportService.formatDateRange(startDate, endDate);
  }

  /**
   * Check if form is valid for generation
   */
  canGenerate(): boolean {
    return !!this.selectedState && !!this.startDate && !!this.endDate;
  }

  /**
   * Generate report name based on state and date range
   */
  private getReportName(): string {
    const stateLabel = this.getStateLabel(this.selectedState);
    const typeLabel = this.getReportTypeLabel(this.reportType);
    const startMonth = new Date(this.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const endMonth = new Date(this.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

    if (startMonth === endMonth) {
      return `${stateLabel} ${typeLabel} - ${startMonth}`;
    }
    return `${stateLabel} ${typeLabel} - ${startMonth} to ${endMonth}`;
  }
}
